/**
 * Client-side protection — soft friction layer.
 *
 * HONEST DISCLAIMER: Everything here is bypass-able. A user with DevTools
 * (or the Network tab, or `curl`) can retrieve the same data our own JS
 * consumes. This module raises the friction bar for casual copiers /
 * students / low-effort scrapers, nothing more. The REAL defence lives
 * server-side in middleware/protection.py.
 *
 * What we block for anonymous / candidate users:
 *   - Right-click context menu (except on inputs/textareas/[data-selectable])
 *   - Text selection via CSS (with the same exceptions)
 *   - Copy / cut events on non-selectable regions
 *   - Devtools shortcuts: F12, Ctrl/Cmd+Shift+I/J/C, Ctrl/Cmd+U, Ctrl/Cmd+S
 *   - Drag-and-drop of images (so people can't yank away assets)
 *
 * What we DO NOT block:
 *   - Any behaviour when the logged-in user is admin / staff / recruiter
 *     (they need devtools + copy for their job)
 *   - Copying inside form inputs (users must be able to paste resumes,
 *     cover letters, generated Content-Studio posts)
 *   - Copying inside anything marked [data-selectable="true"] — Content
 *     Studio outputs mark themselves this way so paying users still get
 *     value out of the tool
 *
 * Toggle via env: REACT_APP_DISABLE_PROTECTION=1 disables the entire layer
 * (useful in local dev).
 */

const DISABLED = process.env.REACT_APP_DISABLE_PROTECTION === "1";

// Roles that get a fully-open experience.
const STAFF_ROLES = new Set(["admin", "staff", "recruiter"]);

// Selectors whose descendants should behave normally (copy, right-click, select).
const SELECTABLE_SELECTOR =
  'input, textarea, [contenteditable="true"], [data-selectable="true"], [data-selectable="true"] *';

function isInsideSelectable(el) {
  if (!el || !(el instanceof Element)) return false;
  return !!el.closest(SELECTABLE_SELECTOR);
}

function blockUnlessSelectable(e) {
  if (isInsideSelectable(e.target)) return;
  e.preventDefault();
  e.stopPropagation();
}

function blockDevtoolsKeys(e) {
  const key = (e.key || "").toLowerCase();
  const mod = e.ctrlKey || e.metaKey;
  // F12
  if (key === "f12") { e.preventDefault(); return; }
  // Ctrl/Cmd + U   (view source)
  if (mod && !e.shiftKey && !e.altKey && key === "u") { e.preventDefault(); return; }
  // Ctrl/Cmd + S   (save page)
  if (mod && !e.shiftKey && !e.altKey && key === "s") { e.preventDefault(); return; }
  // Ctrl/Cmd + Shift + I / J / C  (devtools)
  if (mod && e.shiftKey && (key === "i" || key === "j" || key === "c")) {
    e.preventDefault();
    return;
  }
  // Cmd + Opt + I / J / C  (mac devtools)
  if (e.metaKey && e.altKey && (key === "i" || key === "j" || key === "c")) {
    e.preventDefault();
  }
}

function blockDrag(e) {
  // Only block image drag; allow form-input drag.
  if (e.target && e.target.tagName === "IMG") {
    e.preventDefault();
  }
}

let installed = false;
let currentHandlers = null;

/**
 * Install the client-side protection layer.
 * Call `installProtection(user)` on every mount + role-change.
 * The layer removes itself for staff so devs / admins keep DevTools & copy.
 */
export function installProtection(user) {
  if (DISABLED) return;
  const isStaff = user && STAFF_ROLES.has(user.role);
  if (isStaff) {
    uninstallProtection();
    document.documentElement.removeAttribute("data-karm-protected");
    return;
  }
  if (installed) return;
  installed = true;

  const onContextMenu = (e) => blockUnlessSelectable(e);
  const onCopy = (e) => blockUnlessSelectable(e);
  const onCut = (e) => blockUnlessSelectable(e);
  const onSelectStart = (e) => blockUnlessSelectable(e);
  const onKeyDown = (e) => blockDevtoolsKeys(e);
  const onDragStart = (e) => blockDrag(e);

  document.addEventListener("contextmenu", onContextMenu, true);
  document.addEventListener("copy", onCopy, true);
  document.addEventListener("cut", onCut, true);
  document.addEventListener("selectstart", onSelectStart, true);
  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("dragstart", onDragStart, true);

  currentHandlers = { onContextMenu, onCopy, onCut, onSelectStart, onKeyDown, onDragStart };
  document.documentElement.setAttribute("data-karm-protected", "true");
}

export function uninstallProtection() {
  if (!installed || !currentHandlers) return;
  const h = currentHandlers;
  document.removeEventListener("contextmenu", h.onContextMenu, true);
  document.removeEventListener("copy", h.onCopy, true);
  document.removeEventListener("cut", h.onCut, true);
  document.removeEventListener("selectstart", h.onSelectStart, true);
  document.removeEventListener("keydown", h.onKeyDown, true);
  document.removeEventListener("dragstart", h.onDragStart, true);
  currentHandlers = null;
  installed = false;
  document.documentElement.removeAttribute("data-karm-protected");
}
